import React, { useEffect, useState } from 'react';
import { bool, func, shape, string } from 'prop-types';
import {
  customType,
  extend,
  string as stringType,
  params,
  useLazyDataModel
} from '@thd-nucleus/data-sources';
import { Button, Image } from '@thd-olt-component-react/core-ui';
import classnames from 'classnames';
import { ShareListUrL } from '../share-list-url/share-list-url';
import styles from './settings-form.module.scss';
import { FormHeader } from '../form-header/form-header';
import { NameField } from '../name-field/name-field';
import { ShareToggle } from '../share-toggle/share-toggle';
import { DeleteListButton } from '../delete-list-button/delete-list-button';
import { getValidationError, LIST_SHAPE, triggerAnalytics } from '../helpers';
import { privateAccessType, sharedAccessType, LIST_SETTINGS_SAVE, LIST_DELETE } from '../constants';

export const SettingsForm = (props) => {
  const {
    className, list, onClose, onDelete, onSave
  } = props;
  const [error, setError] = useState(null);
  const { id, name: initialName, listAccessType: initialListAccessType } = list || {};
  const initialShared = initialListAccessType === sharedAccessType;
  const [name, setName] = useState({ value: initialName, valid: true });
  const [shared, setShared] = useState(initialShared);
  const [disableSave, setDisableSave] = useState(true);
  const [saveList, saveResponse] = useLazyDataModel('updateList', {
    context: {
      withAuth: true
    },
    variables: {
      id
    }
  });
  const [isAutoSaving, setIsAutoSaving] = useState(false);

  useEffect(() => {
    LIFE_CYCLE_EVENT_BUS.trigger('list-forms.settings-form-load', {});
  }, []);

  const handleDelete = () => {
    onDelete();
    triggerAnalytics(LIST_DELETE, {});
    onClose();
  };

  const handleCancel = () => {
    onClose();
  };

  const disableSaveButton = () => {
    setDisableSave(true);
  };

  const handleShareToggle = async (event) => {
    event.preventDefault();
    setIsAutoSaving(true);
    const isShared = !shared;
    setShared(isShared);
    const variables = {
      listAccessType: isShared ? sharedAccessType : privateAccessType
    };
    triggerAnalytics(LIST_SETTINGS_SAVE, {
      type: isShared ? 'share' : 'stop sharing'
    });
    await saveList({
      variables,
      update: (cache, { data: responseData }) => {
        const { updateList } = responseData || {};
        if (updateList) {
          cache.modify({
            id: cache.identify(updateList),
            fields: {
              name() {
                return initialName;
              }
            }
          });
        }
      }
    });
    setIsAutoSaving(false);
    onSave();
  };

  const handleSaveClick = async () => {
    disableSaveButton();
    const variables = {};
    if (name.value !== initialName) {
      variables.name = name.value;
      await saveList({
        variables,
        update: (cache, { data: responseData }) => {
          const { updateList } = responseData || {};
          if (updateList) {
            cache.modify({
              id: cache.identify(updateList),
              fields: {
                listAccessType() {
                  return shared ? sharedAccessType : privateAccessType;
                },
              }
            });
          }
        }
      });
      onSave();
    }
  };

  useEffect(() => {
    if (saveResponse?.data?.updateList && saveResponse?.data?.updateList?.name) {
      onClose();
    } else if (saveResponse?.error) {
      setError({ message: saveResponse.error?.message || 'An Error Occurred', name: name.value });
    }
  }, [saveResponse?.data, saveResponse?.error]);

  useEffect(() => {
    const { value, valid } = name;
    const sameName = value === initialName;
    setDisableSave(!valid || sameName);
  }, [name]);

  const handleNameChange = ({ valid, value }) => {
    if (error && value !== error.name) {
      setError(null);
    }
    setName({ valid, value });
    const { error: fieldError } = getValidationError({ value });
    if (fieldError) {
      setError({
        message: fieldError,
        name: value
      });
    }
  };

  const formClasses = classnames(
    styles['settings-form'],
    className
  );

  const handleFormSubmission = async (formSubmission) => {
    formSubmission.preventDefault();
    await handleSaveClick();
  };

  return (
    <form id="list-settings-form" className={formClasses} onSubmit={handleFormSubmission}>
      <FormHeader title="List Settings" onClose={handleCancel} />
      <div className={styles['settings-form__sub-header__container']}>
        <Image
          alt="lock-icon"
          src="https://assets.homedepot-static.com/images/v1/lock-icon-orange.svg"
        />
        <span className={styles['settings-form__sub-header__message']}>
          Your list can only be seen by you or people you share it with
        </span>
      </div>
      <NameField error={error?.message} value={name.value} onValidation={handleNameChange} />
      <ShareToggle isShared={shared} isAutoSaving={isAutoSaving} onShareToggle={handleShareToggle} />
      {shared && <ShareListUrL listId={id} />}
      <div className={styles['settings-form__actions']}>
        <DeleteListButton onClick={disableSaveButton} onDelete={handleDelete} listId={id} />
        <Button type="submit" disabled={disableSave}>Save</Button>
      </div>
    </form>
  );

};

SettingsForm.displayName = 'ListFormsSettingsForm';

SettingsForm.propTypes = {
  className: string,
  onClose: func.isRequired,
  onDelete: func.isRequired,
  list: shape({
    id: string,
    name: string,
    public: bool
  }).isRequired,
  onSave: func
};

SettingsForm.defaultProps = {
  className: null,
  onSave: () => {}
};

SettingsForm.dataModel = extend({
  updateList: params({
    id: stringType().isRequired(),
    name: stringType(),
    listAccessType: customType('AccessTypes').enum(['Private', 'Public', 'Shared'])
  }).mutation().shape(LIST_SHAPE),
}, DeleteListButton);
